<template>
    <div class="ds-ex-row-block" :class="{ 'is-decision': decision && (expanded || compact), 'is-expanded': expanded, 'is-new': isNew }">
        <div class="ds-ex-grid ds-ex-row" :class="compact ? 'ds-ex-grid-month' : 'ds-ex-grid-vacancies'">
            <span v-if="!compact" class="ds-ex-cell-toggle">
                <button
                    v-if="hasList"
                    type="button"
                    class="ds-ex-icon-btn is-small"
                    :aria-label="expanded ? 'Свернуть отклики' : 'Развернуть отклики'"
                    :aria-expanded="expanded"
                    @click="emit('toggle')"
                >
                    <svg class="ds-ex-chevron" :class="{ 'is-open': expanded }" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
                </button>
            </span>
            <div class="ds-ex-when">
                <template v-if="compact">{{ time }}</template>
                <template v-else>
                    <div class="ds-ex-brand" :class="{ 'ds-ex-strong': isToday }">{{ dateLabel }}</div>
                    <div class="ds-ex-muted">{{ time }}</div>
                </template>
            </div>
            <div class="ds-ex-position">
                <span class="ds-ex-ellipsis" :title="vacancy.position.name">{{ vacancy.position.name }}</span>
                <span v-if="isNew" class="ds-ex-chip is-brand is-small">Новая</span>
            </div>
            <span class="ds-ex-muted">{{ EXCHANGE_TYPE_LABELS[vacancy.exchangeType] }}</span>
            <div class="ds-ex-chips-cell">
                <span v-if="chip" class="ds-ex-chip" :class="`is-${chip.tone}`">{{ chip.label }}</span>
                <span v-if="urgent" class="ds-ex-chip is-hot">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2c1 3.5 5 6 5 11a5 5 0 0 1-10 0c0-2.5 1.2-4 2.5-5.2.2 1.7 1 2.7 2 3.2C11 8 11.5 5 12 2z" /></svg>
                    Горящая
                </span>
            </div>
            <div class="ds-ex-responses">
                <template v-if="vacancy.assigned">
                    <ExchangeAvatar :person="vacancy.assigned" />
                    <div class="ds-ex-min0">
                        <div class="ds-ex-brand ds-ex-strong ds-ex-ellipsis">{{ fullName(vacancy.assigned) }}</div>
                        <div class="ds-ex-muted ds-ex-ellipsis">{{ vacancy.assigned.position?.name ?? 'Назначен' }}</div>
                    </div>
                </template>
                <template v-else-if="single">
                    <ExchangeAvatar :person="single.user" />
                    <div class="ds-ex-min0">
                        <div class="ds-ex-brand ds-ex-strong ds-ex-ellipsis">{{ fullName(single.user) }}</div>
                        <div class="ds-ex-muted ds-ex-ellipsis">{{ singleSubtitle }}</div>
                    </div>
                </template>
                <template v-else-if="hasList">
                    <span class="ds-ex-avatar-stack">
                        <ExchangeAvatar v-for="item in vacancy.responses.slice(0, 3)" :key="item.user.id" :person="item.user" ring />
                    </span>
                    <span class="ds-ex-brand ds-ex-strong">{{ countLabel(vacancy.responses.length, RESPONSE_FORMS) }}</span>
                </template>
                <span v-else class="ds-ex-muted">{{ emptyText }}</span>
            </div>
            <div class="ds-ex-actions">
                <template v-if="expanded && hasList">
                    <button type="button" class="ds-ex-link" @click="emit('open', 'history')">История</button>
                </template>
                <template v-else-if="single && decideAllowed">
                    <button type="button" class="ds-glass-btn ds-glass-btn-secondary" :disabled="busy" @click="emit('reject', single.user)">Отклонить</button>
                    <button type="button" class="ds-glass-btn ds-glass-btn-primary" :disabled="busy" @click="emit('confirm', single.user)">Назначить</button>
                </template>
                <template v-else-if="hasList && decideAllowed">
                    <button type="button" class="ds-glass-btn ds-glass-btn-primary" :disabled="busy" @click="emit('toggle')">Выбрать</button>
                </template>
                <button v-if="canEditRow" type="button" class="ds-ex-icon-btn" aria-label="Редактировать" :disabled="busy" @click="emit('open', 'vacancy')">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round" aria-hidden="true"><path d="M4 20h4L19 9l-4-4L4 16v4z" /><path d="M13.5 6.5l4 4" /></svg>
                </button>
                <ExchangeRowMenu v-if="!(expanded && hasList)" :items="menuItems" @select="onMenu" />
            </div>
        </div>
        <div v-if="expanded && hasList" class="ds-ex-responses-list">
            <VacancyResponseItem
                v-for="item in vacancy.responses"
                :key="item.id ?? item.user.id"
                :response="item"
                :can-decide="decideAllowed"
                :busy="busy"
                @confirm="emit('confirm', item.user)"
                @reject="emit('reject', item.user)"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import dayjs from 'dayjs'
import ExchangeAvatar from './ExchangeAvatar.vue'
import ExchangeRowMenu, { type RowMenuItem } from './ExchangeRowMenu.vue'
import VacancyResponseItem from './VacancyResponseItem.vue'
import {
    auditDayKey,
    countLabel,
    dayKeyOf,
    EXCHANGE_TYPE_LABELS,
    fullName,
    hoursUntil,
    isUrgent,
    needsDecision,
    RESPONSE_FORMS,
    rowDateLabel,
    SOURCE_LABELS,
    timeRange,
    todayKey,
    vacancyChip,
    workMs,
} from './exchangeFormat'
import type { UserVacancy, Vacancy } from './exchangeTypes'

const props = defineProps<{
    vacancy: Vacancy
    expanded: boolean
    busy: boolean
    isNew: boolean
    canDecide: boolean
    canManage: boolean
    // Месяц: время без даты (дата — в заголовке дня), без колонки раскрытия.
    compact?: boolean
    now: number
}>()

const emit = defineEmits<{
    toggle: []
    open: [tab: 'vacancy' | 'history']
    confirm: [user: UserVacancy]
    reject: [user: UserVacancy]
    remove: []
}>()

const dayKey = computed(() => dayKeyOf(props.vacancy.start))
const isToday = computed(() => dayKey.value === todayKey())
const dateLabel = computed(() => rowDateLabel(dayKey.value))
const time = computed(() => timeRange(props.vacancy))
const chip = computed(() => vacancyChip(props.vacancy))
const urgent = computed(() => isUrgent(props.vacancy, props.now))
const decision = computed(() => needsDecision(props.vacancy))
// Фон «Нужно решение» как в A2: неделя — только раскрытая вакансия (01), месяц — каждая строка с решением (03).
// Отклики и действия по ним — только у вакансии, ждущей решения (не у назначенной/закрытой).
const hasList = computed(() => decision.value && props.vacancy.responses.length > 1)
const single = computed(() => decision.value && props.vacancy.responses.length === 1 ? props.vacancy.responses[0] : null)
const singleSubtitle = computed(() => {
    const item = single.value
    if (!item) return ''
    return [item.user.position?.name, item.source ? SOURCE_LABELS[item.source].toLowerCase() : item.user.department?.name].filter(Boolean).join(' · ')
})
// С начала смены назначение запрещено (сервер проверяет сам: vacancy_start_passed).
const decideAllowed = computed(() => props.canDecide && workMs(props.vacancy.start) > props.now)
// Правка — только по can_edit backend.
const canEditRow = computed(() => props.canManage && props.vacancy.canEdit)

const emptyText = computed(() => {
    if (props.isNew) return 'Опубликована только что'
    const parts = ['Нет откликов']
    if (urgent.value) parts.push(`начало через ${hoursUntil(props.vacancy, props.now)} ч`)
    else if (props.vacancy.createdAt) {
        const created = auditDayKey(props.vacancy.createdAt)
        const today = todayKey()
        if (created === today) parts.push('опубликована сегодня')
        else if (created === dayjs(today).subtract(1, 'day').format('YYYY-MM-DD')) parts.push('опубликована вчера')
    }
    return parts.join(' · ')
})

const menuItems = computed<RowMenuItem[]>(() => {
    const items: RowMenuItem[] = [{ id: 'history', label: 'История' }]
    if (props.canManage && props.vacancy.canDelete) items.push({ id: 'remove', label: 'Снять вакансию', danger: true, disabled: props.busy })
    return items
})

function onMenu(id: string) {
    if (id === 'history') emit('open', 'history')
    else if (id === 'remove') emit('remove')
}
</script>
